import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Flex, Input, Modal, Result, Tag, Typography } from 'antd';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from './auth.tsx';
import { getOwnPayee } from './payee-api.ts';
import { ApiRequestError } from './project-api.ts';
import { exportBankAccount, getOwnBankAccount } from './bank-account-api.ts';

const { Paragraph, Text, Title } = Typography;

export function PayeePage() {
  const { state } = useAuth();
  const token = state.kind === 'ready' ? state.token : '';
  const query = useQuery({ queryKey: ['own-payee', token], queryFn: () => getOwnPayee(token),
    enabled: token.length > 0, retry: false });
  const account = useQuery({ queryKey: ['own-bank-account', token], queryFn: () => getOwnBankAccount(token),
    enabled: token.length > 0, retry: false });
  const exporter = useMutation({ mutationFn: exportBankAccount });
  const [purpose, setPurpose] = useState('');
  const [exportOpen, setExportOpen] = useState(false);
  const [exportNotice, setExportNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const notConfigured = query.error instanceof ApiRequestError && query.error.status === 404 &&
    query.error.code === 'PAYEE_NOT_CONFIGURED';

  return <main className="page-shell payee-shell">
    <Flex justify="space-between" align="center" gap={16} wrap>
      <div><Text type="secondary">P1 身份边界</Text><Title level={2}>我的收款身份</Title></div>
      <Link to="/projects">返回我的项目</Link>
    </Flex>
    <Alert type="info" showIcon title="登录账号、人员和收款对象分开管理"
      description="本页只显示本人收款身份和银行账户；银行账户明文查看会记录审计，不包含结算金额或付款信息。" />
    {exportNotice && <Alert className="workspace-alert" showIcon type={exportNotice.type} title={exportNotice.text}
      closable onClose={() => setExportNotice(null)} />}
    {query.isPending && <Card loading className="payee-card" />}
    {notConfigured && <Result status="info" title="尚未建立收款对象"
      subTitle="这不会伪造默认收款人。请由行政在人员关系确认后建立。" />}
    {query.isError && !notConfigured && <Alert type="error" showIcon title="无法读取本人收款对象"
      description={query.error instanceof Error ? query.error.message : '查询失败'} />}
    {query.data && <Card className="payee-card" variant="borderless">
      <Flex justify="space-between" align="start" gap={18} wrap>
        <div><Text type="secondary">{query.data.personCode}</Text><Title level={3}>{query.data.displayName}</Title></div>
        <Tag color={query.data.status === 'ACTIVE' ? 'success' : 'default'}>
          {query.data.status === 'ACTIVE' ? '收款对象有效' : '收款对象已停用'}
        </Tag>
      </Flex>
      <dl className="status-list">
        <div><dt>人员类型</dt><dd>{query.data.engagementType === 'EMPLOYEE' ? '全职员工' : '长期合作执行人员'}</dd></div>
        <div><dt>结算周期类型</dt><dd>{query.data.settlementCycle === 'MONTHLY' ? '自然月' : '周一至周日'}</dd></div>
        <div><dt>收款对象 ID</dt><dd>{query.data.payeeId}</dd></div>
        <div><dt>人员 ID</dt><dd>{query.data.personId}</dd></div>
      </dl>
      <Paragraph type="secondary">本页不是预结算单；费用来源、应计、结算和付款仍按后续阶段实现。</Paragraph>
    </Card>}
    {account.isPending && <Card loading className="payee-card" />}
    {account.error instanceof ApiRequestError && account.error.status === 404 && <Card className="payee-card" variant="borderless">
      <Result status="info" title="尚未录入银行账户" subTitle="请联系行政核对身份后录入。" />
    </Card>}
    {account.isError && !(account.error instanceof ApiRequestError && account.error.status === 404) &&
      <Alert type="error" showIcon title="无法读取本人银行账户"
        description={account.error instanceof Error ? account.error.message : '查询失败'} />}
    {account.data && <Card title="本人收款账户" className="payee-card" variant="borderless">
      <dl className="status-list">
        <div><dt>账户名</dt><dd>{account.data.accountHolder}</dd></div>
        <div><dt>开户行</dt><dd>{account.data.bankName}</dd></div>
        <div><dt>银行账号</dt><dd className="account-number">{account.data.accountNumber}</dd></div>
        <div><dt>数据版本</dt><dd>v{account.data.version}</dd></div>
      </dl>
      <Button onClick={() => { setPurpose(''); setExportOpen(true); }}>填写用途并导出</Button>
    </Card>}
    <Modal title="导出本人收款账户" open={exportOpen} footer={null} destroyOnHidden
      onCancel={() => !exporter.isPending && setExportOpen(false)}>
      <Alert type="info" showIcon title="仅能在拥有银行账户独立导出权时下载"
        description="用途、导出人、时间和文件范围会记录审计。" />
      <p>导出用途（至少 10 字）：</p>
      <Input.TextArea value={purpose} maxLength={500} rows={3} onChange={(event) => setPurpose(event.target.value)} />
      <Flex justify="end" gap={10} className="customer-modal-actions"><Button onClick={() => setExportOpen(false)}>取消</Button>
        <Button type="primary" loading={exporter.isPending} disabled={purpose.trim().length < 10} onClick={() => {
          if (!account.data) return;
          void exporter.mutateAsync({ token, payeeId: account.data.payeeId, purpose }).then(({ file }) => {
            const url = URL.createObjectURL(file); const anchor = document.createElement('a');
            anchor.href = url; anchor.download = `bank-account-${account.data.personCode}.csv`; anchor.click();
            URL.revokeObjectURL(url); setExportOpen(false); setPurpose('');
            setExportNotice({ type: 'success', text: '已下载带水印的账户文件。' });
          }).catch((error: unknown) => setExportNotice({ type: 'error', text: error instanceof Error ? error.message : '导出失败' }));
        }}>确认并下载</Button></Flex>
    </Modal>
  </main>;
}
